import axios from 'axios';
import baseUrlConfig from '../config/baseUrl';
//workflow: API gives 417 →First time → Call refresh token API->If refresh succeeds → Retry the original API->If refresh itself gives 417 → Logout (session expired).

class HttpService {
  constructor() {
    this.axios = axios.create({
      baseURL: baseUrlConfig.baseUrl,
      // Remove withCredentials from defaults - we'll handle it per request
      headers: { 'Content-Type': 'application/json' }
    });
    
    this.isRefreshing = false;
    this.failedRequests = [];
    this.isLoggingOut = false;
    
    this.httpInterceptors();
  }

  httpInterceptors() {
    this.axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't intercept requests that skip cookies (like password reset)
        if (originalRequest.skipCookies) {
          return Promise.reject(error);
        }

        if (
          error.response?.status === 417 &&
          !originalRequest._retry &&
          !originalRequest.url.includes('/token/refresh/')
        ) {
          // If we're already refreshing, queue this request
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedRequests.push({ resolve, reject, originalRequest });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            // Only make ONE refresh call
            await this.axios.post('v0/api/users/token/refresh/', {}, { 
              withCredentials: true 
            });
            
            // Retry the original request that triggered the refresh
            const response = await this.axios(originalRequest);
            
            // Process all queued requests
            this.processFailedRequests();
            
            return response;
          } catch (refreshErr) {
            // If refresh fails, reject all queued requests and logout
            this.failedRequests.forEach(failedRequest => {
              failedRequest.reject(refreshErr);
            });
            
            if (refreshErr.response?.status === 417) {
              await this.handleLogout();
            }
            
            return Promise.reject(refreshErr);
          } finally {
            this.isRefreshing = false;
            this.failedRequests = [];
          }
        }

        // If refresh token API itself returns 417, logout
        if (error.response?.status === 417 && originalRequest.url.includes('/token/refresh/')) {
          await this.handleLogout();
        }

        return Promise.reject(error);
      }
    );
  }

  processFailedRequests() {
    const promises = this.failedRequests.map(failedRequest => {
      return this.axios(failedRequest.originalRequest)
        .then(failedRequest.resolve)
        .catch(failedRequest.reject);
    });
    
    this.failedRequests = [];
    
    return Promise.all(promises);
  }

  async handleLogout() {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    console.log('Logout initiated');

    try {
      localStorage.removeItem('is_loggedin');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      setTimeout(() => {
        window.location.reload();
      }, 100);
      this.isLoggingOut = false;
    }
  }

  async fetch(payload, cancelToken) {
    const config = {
      url: payload.url,
      method: payload.method || 'GET',
      params: payload.params,
      data: payload.data ? JSON.stringify(payload.data) : undefined,
      cancelToken,
      headers: {
        ...this.axios.defaults.headers,
        ...payload.headers
      },
      // Only send credentials if not explicitly skipped
      withCredentials: !payload.skipCookies
    };
    
    return this.axios(config);
  }
}

const httpService = new HttpService();
export default httpService;